import { gql } from "@apollo/client";
import { POByNumber } from "../../modules/entityModules/CPOImport/query";

const updatePO = /* GraphQL */ `
  mutation UpdatePO($input: UpdatePOInput!, $condition: ModelPOConditionInput) {
    updatePO(input: $input, condition: $condition) {
      id
      number
      itemNumber
      price
      priceCur
      materialEntityRelation {
        id
        code
      }
      __typename
    }
  }
`;

/**
 * Updates PO prices based on CSV data
 * @param {Object} params
 * @param {Object} params.client - Apollo client instance
 * @param {Array} params.csvRows - Array of CSV row objects with: Code, PO number, Open Quantity, added charge
 * Note: "added charge" column contains the NEW price (not an amount to add to current price)
 * @returns {Promise<Object>} Summary of updates
 */
export default async function updatePOPricesFromCSV({ client, csvRows }) {
  const results = {
    found: [],
    notFound: [],
    errors: [],
    summary: {
      totalRows: csvRows.length,
      updated: 0,
      notFound: 0,
      errors: 0,
    },
  };

  // Process each row
  for (const row of csvRows) {
    const code = row.Code?.toString().trim();
    const poNumber = row["PO number"]?.toString().trim();
    // The "added charge" column contains the NEW price (not an amount to add)
    const newPrice = parseFloat(row["added charge"] || row["add upcharge"]);

    // Skip empty rows
    if (!code || !poNumber || isNaN(newPrice)) {
      if (code || poNumber) {
        results.notFound.push({
          row,
          reason: "Missing code, PO number, or invalid new price value",
        });
        results.summary.notFound++;
      }
      continue;
    }

    try {
      // Try different PO number formats
      const poNumberVariants = [
        poNumber, // Try as-is first
        `A6P-${poNumber}`, // Try with A6P prefix
        `F6P-${poNumber}`, // Try with F6P prefix
      ];

      let poFound = null;
      let lastError = null;

      for (const variant of poNumberVariants) {
        try {
          const { data } = await client.query({
            query: gql(POByNumber),
            variables: {
              number: variant,
            },
          });

          const items = data?.POByNumber?.items || [];

          // Filter by code
          const matchingPO = items.find(
            (po) => po.materialEntityRelation?.code === code
          );

          if (matchingPO) {
            poFound = matchingPO;
            break;
          }
        } catch (error) {
          lastError = error;
          continue;
        }
      }

      if (!poFound) {
        results.notFound.push({
          row,
          reason: `PO not found for number "${poNumber}" with code "${code}"`,
        });
        results.summary.notFound++;
        continue;
      }

      // The new price is directly from the CSV (not current price + upcharge)
      const currentPrice = parseFloat(poFound.price) || 0;
      const priceChange = newPrice - currentPrice;

      results.found.push({
        row,
        po: {
          id: poFound.id,
          number: poFound.number,
          itemNumber: poFound.itemNumber,
          code: poFound.materialEntityRelation?.code,
          currentPrice,
          newPrice,
          priceChange,
          priceCur: poFound.priceCur,
        },
      });
    } catch (error) {
      results.errors.push({
        row,
        error: error.message,
      });
      results.summary.errors++;
    }
  }

  return results;
}

/**
 * Executes the price updates
 * @param {Object} params
 * @param {Object} params.client - Apollo client instance
 * @param {Array} params.updates - Array of update objects from updatePOPricesFromCSV results
 * @returns {Promise<Array>} Array of update results
 */
export async function executePriceUpdates({ client, updates }) {
  const updateResults = [];

  for (const item of updates) {
    try {
      const { data } = await client.mutate({
        mutation: gql(updatePO),
        variables: {
          input: {
            id: item.po.id,
            price: item.po.newPrice,
          },
        },
      });

      updateResults.push({
        success: true,
        po: item.po,
        updated: data.updatePO,
      });
    } catch (error) {
      updateResults.push({
        success: false,
        po: item.po,
        error: error.message,
      });
    }
  }

  return updateResults;
}

